import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { get, isEmpty, isEqual } from 'lodash';
import { addAdressAction, deleteAdressAction, setActiveModalAction } from '../../../actions';
import { useOnClickOutside } from '../../../hooks';
import { fromStore } from '../../../selectors';
import { adressFields } from '../../../constants';
import { getAdress, showNotification } from '../../../utils';
import { Icon, IconTypes } from '../../../components';

const getDefaultValues = (user) => {
  const fields = {};
  Object.entries(adressFields).forEach(([field, values]) => {
    fields[field] = get(user, field, values.defaultValue);
  });
  return fields;
};

export function AddressesModal() {
  const dispatch = useDispatch();
  const ref = useRef();
  const user = useSelector(fromStore.userSelector);
  const orderAdress = useSelector(fromStore.orderDeliveryAdressSelector);
  const loader = useSelector(fromStore.adressLoaderSelector);
  const city = useSelector(fromStore.selectedCitySelector);
  const { addresses } = user;
  const form = useForm({ defaultValues: getDefaultValues(orderAdress) });
  const { handleSubmit } = form;

  const [selectedAdress, setSelectedAdress] = useState(null);
  const [filteredAddresses, setFilteredAddresses] = useState([]);

  useEffect(() => {
    if (!isEmpty(addresses) && !isEmpty(orderAdress)) {
      const entity = addresses.find((address) => {
        const dataAddress = {
          street: address.street || null,
          house: address.house || null,
          room: address.room || null,
          entrance: address.entrance || null,
          privateHouse: address.privateHouse || false,
          area: address.area || null,
          floor: address.floor || null,
        };
        const dataOrder = {
          street: orderAdress.street || null,
          house: orderAdress.house || null,
          room: orderAdress.room || null,
          entrance: orderAdress.entrance || null,
          privateHouse: orderAdress.privateHouse || false,
          area: orderAdress.area || null,
          floor: orderAdress.floor || null,
        };
        return isEqual(dataAddress, dataOrder);
      });
      setSelectedAdress(entity || null);
    }
  }, [orderAdress, addresses]);

  const onClose = () => {
    if (loader) return;
    dispatch(setActiveModalAction({ field: 'addresses', value: false }));
  };

  useEffect(() => {
    if (isEmpty(user) || isEmpty(user.addresses) || isEmpty(city)) onClose();
    let result = user.addresses;
    result = result.filter((address) => address.cityId === city.id);
    setFilteredAddresses(result);
  }, [user, user.addresses, city]);

  const onSubmit = () => {
    const dataAddres = {
      street: selectedAdress.street || null,
      house: selectedAdress.house || null,
      room: selectedAdress.room || null,
      entrance: selectedAdress.entrance || null,
      privateHouse: selectedAdress.privateHouse || false,
      area: selectedAdress.area || null,
      floor: selectedAdress.floor || null,
      coords: selectedAdress.coords || null,
    };
    dispatch(addAdressAction(dataAddres));
    onClose();
  };

  const handleDelete = (address) => {
    showNotification({
      title: 'Удаление адреса',
      text: `Вы действительно хотите удалить адресс: ${getAdress(address, city.name)}`,
      icon: 'warning',
      confirmButtonText: 'Удалить',
      cancelButtonText: 'Отмена',
      showCancelButton: true,
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-light',
      },
    }).then((confirm) => {
      if (confirm.isConfirmed) dispatch(deleteAdressAction(address.id));
    });
  };

  useOnClickOutside(ref, onClose);

  const renderCloseButton = (
    <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={onClose} aria-hidden>
      <span className="svg-icon svg-icon-1">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
          <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
        </svg>
      </span>
    </div>
  );

  const renderButtons = (
    <div className="text-center">
      <button onClick={onClose} disabled={loader} type="button" className="btn btn-light me-3">Отмена</button>
      <button disabled={loader || isEmpty(selectedAdress)} type="submit" className="btn btn-primary">
        {loader ? (
          <span className="indicator-progress d-block">
            Сохранение...
            <span className="spinner-border spinner-border-sm align-middle ms-2" />
          </span>
        ) : (
          <span className="indicator-label">Сохранить</span>
        )}
      </button>
    </div>
  );

  const renderAdresses = (
    <div className="d-flex flex-column gap-3 mb-3">
      {filteredAddresses.map((address) => (
        <div key={address.id} className="col-lg-12 d-flex align-items-center border border-secondary rounded p-3 flex-stack">
          {// eslint-disable-next-line react/jsx-curly-brace-presence, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div onClick={() => setSelectedAdress(address)} className="d-flex align-items-center gap-3" style={{ cursor: 'pointer' }}>
              <span className="form-check form-check-custom form-check-solid">
                <input className="form-check-input" type="radio" value="fast" checked={selectedAdress && selectedAdress.id === address.id} />
              </span>
              <div className="d-flex flex-wrap">
                {getAdress(address, city.name)}
              </div>
            </div>
          }
          <div>
            <button onClick={() => handleDelete(address)} disabled={loader} type="button" className="btn btn-sm btn-icon btn-light btn-active-white ms-1 btn-hover-scale">
              <Icon type={IconTypes.trash} className="svg-icon svg-icon-3" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="modal fade d-block show">
      <div className="modal-dialog modal-lg mw-650px">
        <div ref={ref} className="modal-content rounded">
          <div className="modal-header pb-0 border-0 justify-content-end">
            {renderCloseButton}
          </div>
          <div className="modal-body scroll-y px-10 px-lg-15 pt-0 pb-15">
            <form onSubmit={handleSubmit(onSubmit)} className="form fv-plugins-bootstrap5 fv-plugins-framework">
              <div className="mb-13 text-center">
                <h1 className="mb-3">Выбор адреса доставки</h1>
              </div>
              {renderAdresses}
              {renderButtons}
              <div />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
